import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { LogIn, User, Menu, X, UserRound, MessageCircle } from "lucide-react";
import logo from "/lovable-uploads/5639bd1d-d899-4190-af54-f5f73944d997.png";
import { supabase } from "@/integrations/supabase/client";
import { useRole } from "@/hooks/useRole";
import { useToast } from "@/components/ui/use-toast";
import { useIsMobile } from "@/hooks/use-mobile";
import { UserProfileSection } from "./navbar/UserProfileSection";
import { useHeroBackground } from "@/hooks/useHeroBackground";
// import { NotificationButton } from "@/components/ui/NotificationButton"; // TEMPORARIAMENTE COMENTADO

const navLinks = [
  { to: "/", label: "Início" },
  { to: "/mentores", label: "Mentores" },
  { to: "/comunidades", label: "Comunidades" },
  { to: "/jogos", label: "Jogos" },
  { to: "/mentorias", label: "Mentorias" },
  { to: "/como-funciona", label: "Como Funciona" },
];

const Navbar = () => {
  const location = useLocation();
  const [session, setSession] = useState<any>(null);
  const { role, authUser, loading: roleLoading } = useRole();
  const { toast } = useToast();
  const isMobile = useIsMobile();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { settings } = useHeroBackground();

  // Verifica se estamos na home page e há uma imagem de fundo
  const isHomePage = location.pathname === "/";
  const hasHeroBackground = isHomePage && settings?.background_image_url;
  
  // Verifica se estamos em páginas de painel (onde não deve mostrar menu hamburger)
  const isPanelPage = location.pathname.startsWith('/painel-admin') || 
                      location.pathname.startsWith('/painel-mentor') || 
                      location.pathname.startsWith('/painel-jogador');

  useEffect(() => {
    // Listener de sessão
    supabase.auth.getSession().then(({ data }) => setSession(data?.session));
    const { data: listener } = supabase.auth.onAuthStateChange((_event, session) => setSession(session));
    return () => {
      listener?.subscription.unsubscribe();
    };
  }, []);

  // Close mobile menu when changing routes
  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location]);

  const handleLogout = async () => {
    await supabase.auth.signOut();
    setSession(null);
    window.location.href = "/";
  };
  
  // Verifica se o usuário está autenticado mas não tem um perfil
  const isAuthenticated = !!session;
  const hasNoProfile = isAuthenticated && authUser && !role && !roleLoading;
  
  // Apenas mostrar aviso de erro se usuário estiver autenticado
  if (hasNoProfile && isAuthenticated) {
    console.warn("[Navbar] Usuário autenticado sem perfil:", authUser?.email);
  }

  return (
    <nav className={`w-full border-b border-[#222A35] shadow-md fixed top-0 left-0 z-50 transition-all duration-300 ${
      hasHeroBackground 
        ? "bg-[#181D24]/60 backdrop-blur-lg border-white/10" 
        : "bg-[#181D24]"
    }`}>
      <div className="max-w-7xl mx-auto flex items-center justify-between px-4 sm:px-6 py-2">
        <Link to="/" className="flex items-center gap-2">
          <img 
            src={logo} 
            alt="Arena Pro Player Logo" 
            className={`h-14 sm:h-16 w-auto transition-all duration-300 ${
              hasHeroBackground ? "drop-shadow-lg" : ""
            }`} 
          />
        </Link>
        
        {/* Desktop Navigation */}
        <div className="hidden md:flex gap-6 items-center">
          {navLinks.map((link) => (
            <Link
              to={link.to}
              key={link.to}
              className={`font-semibold text-sm transition hover:text-[#00B6F0] ${
                location.pathname === link.to
                  ? "text-[#00B6F0]"
                  : "text-white"
              } ${hasHeroBackground ? "drop-shadow-md" : ""}`}
            >
              {link.label}
            </Link>
          ))}
        </div>

        {/* Mobile Menu Button - Apenas para páginas não-painel */}
        {!isPanelPage && (
          <button 
            className={`md:hidden text-white p-2 rounded-md hover:bg-white/10 transition-all duration-200 z-50 ${
              hasHeroBackground ? "drop-shadow-md backdrop-blur-sm bg-black/20" : "bg-[#242C37]"
            }`}
            onClick={() => {
              setMobileMenuOpen(!mobileMenuOpen);
            }}
            aria-label="Toggle menu"
          >
            {mobileMenuOpen ? (
              <X size={24} className="text-white" />
            ) : (
              <Menu size={24} className="text-white" />
            )}
          </button>
        )}
        
        {/* Authentication Section - Desktop */}
        <div className="hidden md:flex gap-3 items-center">
          {session ? (
            <>
              {hasNoProfile && isAuthenticated ? (
                <button 
                  onClick={() => toast({
                    title: "Perfil não encontrado",
                    description: "Seu perfil não foi encontrado. Entre em contato com o administrador do sistema.",
                    variant: "destructive",
                  })}
                  className="flex items-center px-4 py-2 rounded bg-[#FF5555] text-white font-bold transition gap-1 w-full justify-center"
                >
                  Erro de Perfil
                </button>
              ) : (
                <>
                  <UserProfileSection role={role} onLogout={handleLogout} />
                  {/* <NotificationButton /> */}
                </>
              )}
            </>
          ) : (
            <>
              <Link to="/login" className={isMobile ? 'w-full' : ''}>
                <button className={`flex items-center px-4 py-2 rounded bg-[#242C37] text-white hover:bg-[#00B6F0] hover:text-black font-bold transition gap-1 w-full justify-center ${
                  hasHeroBackground ? "backdrop-blur-sm bg-[#242C37]/80 shadow-lg" : ""
                }`}>
                  <LogIn size={18} /> Login
                </button>
              </Link>
              <Link to="/cadastro" className={isMobile ? 'w-full' : ''}>
                <button className={`flex items-center px-4 py-2 rounded bg-[#00B6F0] text-black font-bold hover:bg-[#1AD3F9] transition gap-1 w-full justify-center ${
                  hasHeroBackground ? "shadow-lg" : ""
                }`}>
                  <User size={18} /> Cadastrar-se
                </button>
              </Link>
            </>
          )}
        </div>
      </div>
      
      {/* Mobile Navigation Links - Apenas para páginas não-painel */}
      {!isPanelPage && mobileMenuOpen && (
        <div className={`md:hidden py-4 px-4 border-t border-[#222A35] ${
          hasHeroBackground 
            ? "bg-[#1A2233]/95 backdrop-blur-md border-white/10" 
            : "bg-[#1A2233]"
        }`}>
          {navLinks.map((link) => (
            <Link
              to={link.to}
              key={link.to}
              className={`block py-2 px-2 font-semibold text-base transition hover:text-[#00B6F0] ${
                location.pathname === link.to
                  ? "text-[#00B6F0]"
                  : "text-white"
              }`}
            >
              {link.label}
            </Link>
          ))}
          
          {/* Profile Button - Only show if user is authenticated */}
          {session && role && (
            <Link
              to={`/painel-${role === 'admin' ? 'admin' : role === 'mentor' ? 'mentor' : 'jogador'}/perfil`}
              className="block py-2 px-2 mt-3 border-t border-[#222A35] pt-3"
            >
              <div className="flex items-center gap-3 px-2 py-2 rounded-lg bg-[#223342] hover:bg-[#2A3F56] transition-all duration-200">
                <UserRound className="w-5 h-5 text-[#00B6F0]" />
                <span className="font-semibold text-white">Meu Perfil</span>
              </div>
            </Link>
          )}
          
          {/* Fale Conosco Button */}
          <button
            onClick={() => {
              const whatsappUrl = 'https://wa.me/5511996425204?text=Ol%C3%A1%2C%20vim%20atrav%C3%A9s%20do%20site%20Arena%20Pro%20Player%20e%20gostaria%20de%20ajuda.';
              window.open(whatsappUrl, '_blank');
            }}
            className="block py-2 px-2 mt-3"
          >
            <div className="flex items-center gap-3 px-2 py-2 rounded-lg bg-[#25D366] hover:bg-[#20BA5A] transition-all duration-200">
              <MessageCircle className="w-5 h-5 text-white" />
              <span className="font-semibold text-white">Fale Conosco</span>
            </div>
          </button>
          
          {/* Mobile Auth Buttons */}
          <div className="mt-4 space-y-3">
            {session ? (
              <>
                {hasNoProfile && isAuthenticated ? (
                  <button 
                    onClick={() => toast({
                      title: "Perfil não encontrado",
                      description: "Seu perfil não foi encontrado. Entre em contato com o administrador do sistema.",
                      variant: "destructive",
                    })}
                    className="flex items-center px-4 py-2 rounded bg-[#FF5555] text-white font-bold transition gap-1 w-full justify-center"
                  >
                    Erro de Perfil
                  </button>
                ) : (
                  <>
                    <UserProfileSection role={role} onLogout={handleLogout} />
                    <button
                      onClick={handleLogout}
                      className="flex items-center px-4 py-2 rounded bg-red-600 text-white hover:bg-red-700 font-bold transition gap-1 w-full justify-center mt-2"
                    >
                      <LogIn size={18} className="rotate-180" /> Sair
                    </button>
                    {/* <NotificationButton /> */}
                  </>
                )}
              </>
            ) : (
              <>
                <Link to="/login" className="w-full">
                  <button className="flex items-center px-4 py-2 rounded bg-[#242C37] text-white hover:bg-[#00B6F0] hover:text-black font-bold transition gap-1 w-full justify-center">
                    <LogIn size={18} /> Login
                  </button>
                </Link>
                <Link to="/cadastro" className="w-full">
                  <button className="flex items-center px-4 py-2 rounded bg-[#00B6F0] text-black font-bold hover:bg-[#1AD3F9] transition gap-1 w-full justify-center">
                    <User size={18} /> Cadastrar-se
                  </button>
                </Link>
              </>
            )}
          </div>
        </div>
      )}

    </nav>
  );
};

export default Navbar;
